import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { products } from "@/mocks/products";
import type { Product } from "@/mocks/products";

interface Props {
  product: Product;
}

const categoryColor: Record<string, string> = {
  dzi: "#8b6f47",
  crystal: "#7a6a9a",
  jade: "#4a7a52",
  thangka: "#c9a96e",
};

export default function ProductDetailRelated({ product }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  const related = products
    .filter((p) => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  if (related.length === 0) return null;

  const color = categoryColor[product.category];

  return (
    <section className="py-16 bg-[#faf8f4]" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        <div
          className={`transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-px" style={{ backgroundColor: color }}></span>
                <span
                  className="text-[0.9375rem] tracking-[0.25em] font-medium"
                  style={{ color, fontFamily: "'Noto Serif TC', serif" }}
                >
                  RELATED
                </span>
              </div>
              <h2
                className="text-[#2d2520] font-bold leading-[1.4]"
                style={{ fontSize: "clamp(1.375rem, 2.5vw, 1.875rem)", fontFamily: "'Noto Serif TC', serif" }}
              >
                同系列商品
              </h2>
            </div>
            <button
              onClick={() => navigate(`/products?category=${product.category}`)}
              className="flex items-center gap-2 text-[0.9375rem] font-medium cursor-pointer hover:gap-3 transition-all duration-200 whitespace-nowrap"
              style={{ color, fontFamily: "'Noto Serif TC', serif" }}
            >
              查看全部
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((p, i) => (
              <div
                key={p.id}
                className="group cursor-pointer"
                style={{
                  transitionDelay: `${i * 80}ms`,
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(20px)",
                  transition: "opacity 0.7s ease, transform 0.7s ease",
                }}
                onClick={() => navigate(`/products/${p.id}`)}
              >
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-white mb-4">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#1e1a16]/0 group-hover:bg-[#1e1a16]/30 transition-all duration-300 flex items-center justify-center">
                    <span
                      className="px-4 py-2 bg-white text-[#2d2520] rounded-full text-[0.875rem] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
                      style={{ fontFamily: "'Noto Serif TC', serif" }}
                    >
                      查看詳情
                    </span>
                  </div>
                  {p.badge && (
                    <div className="absolute top-3 left-3">
                      <span
                        className="px-3 py-1 text-white text-[0.8125rem] rounded-full font-medium"
                        style={{ backgroundColor: color, fontFamily: "'Noto Serif TC', serif" }}
                      >
                        {p.badge}
                      </span>
                    </div>
                  )}
                </div>
                <h3
                  className="text-[#2d2520] font-semibold mb-1 leading-[1.4] text-[0.9375rem]"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  {p.name}
                </h3>
                <p
                  className="font-semibold text-[0.9375rem]"
                  style={{ color, fontFamily: "'Noto Serif TC', serif" }}
                >
                  NT$ {p.price.toLocaleString("zh-TW")}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
